import type { User } from "@assetwatch/auth";

type PendoQueue = {
  _q: unknown[][];
  [method: string]: unknown;
};

type PendoApi = {
  initialize: (options: {
    visitor: PendoVisitor;
    account?: PendoAccount;
    location?: typeof PENDO_LOCATION;
  }) => void;
  isReady?: () => boolean;
  clearSession?: () => void;
  teardown?: () => void;
};

type PendoGlobal = PendoApi | PendoQueue;

declare global {
  interface Window {
    pendo?: PendoGlobal;
  }
}

/** Strips the query string from collected URLs; Work search params can hold free text. */
const PENDO_LOCATION = {
  transforms: [{ attr: "search", action: "Clear" }],
} as const;

export type PendoVisitor = { id: string; roles: string[] };
export type PendoAccount = { id: string; name: string };

const PENDO_METHODS = [
  "initialize",
  "identify",
  "updateOptions",
  "pageLoad",
  "track",
] as const;

export function getPendoApiKey(rawKey?: string) {
  const key = rawKey?.trim();

  return key ? key : undefined;
}

export function buildPendoVisitor(
  user: Pick<User, "sub" | "role" | "roles">,
): PendoVisitor {
  const roles = user.roles && user.roles.length > 0 ? user.roles : [user.role];

  return { id: user.sub, roles };
}

/** The account is the selected customer: external customer ID plus its name (ID when unnamed). */
export function buildPendoAccount(customer?: {
  id: string;
  name?: string;
}): PendoAccount | undefined {
  const id = customer?.id.trim();
  if (!id) {
    return undefined;
  }

  return { id, name: customer?.name?.trim() || id };
}
const LOCAL_HOSTNAMES = ["localhost", "127.0.0.1", "[::1]", "::1"];

export function isLocalhost(hostname: string = window.location.hostname) {
  return LOCAL_HOSTNAMES.includes(hostname);
}

/** Page-view payload carries the path only; search params can hold free text. */
export function trackPendoPageView(pathname: string) {
  const pendo = window.pendo as
    | { pageLoad?: () => void; track?: (n: string, m: object) => void }
    | undefined;

  pendo?.pageLoad?.();
  pendo?.track?.("Work Page Viewed", { pathname, sourceApp: "work" });
}

function installPendoStub(pendo: PendoQueue) {
  for (const method of PENDO_METHODS) {
    pendo[method] = (...args: unknown[]) => {
      pendo._q[method === "initialize" ? "unshift" : "push"]([method, ...args]);
    };
  }
}

export function loadPendoAgent(apiKey: string) {
  if (window.pendo) {
    return;
  }

  const pendo: PendoQueue = { _q: [] };
  installPendoStub(pendo);
  window.pendo = pendo;

  const script = document.createElement("script");
  script.async = true;
  script.dataset.pendoAgent = "true";
  script.src = `https://cdn.pendo.io/agent/static/${encodeURIComponent(apiKey)}/pendo.js`;
  document.head.appendChild(script);
}

export function initializePendo(
  apiKey: string,
  visitor: PendoVisitor,
  account?: PendoAccount,
) {
  loadPendoAgent(apiKey);
  (window.pendo as PendoApi).initialize(
    account
      ? { visitor, account, location: PENDO_LOCATION }
      : { visitor, location: PENDO_LOCATION },
  );
}

/** Drops stored visitor state so the next visitor is not attributed to this one. */
export function clearPendoSession() {
  (window.pendo as PendoApi | undefined)?.clearSession?.();
}

export function teardownPendo() {
  (window.pendo as PendoApi | undefined)?.teardown?.();
  document.querySelector("script[data-pendo-agent]")?.remove();
  delete window.pendo;
}
